// src/features/dashboard/components/EmployeeActiveHoursReport/ActiveHoursHistory30Days.tsx

import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';

import {
  ActiveHoursHistory30DaysResponse,
} from '../../types/EmployeeActiveHours/employeeActiveHours.types';

interface Props {
  data: ActiveHoursHistory30DaysResponse;
}

/* -------------------------------------------------------------------------- */
/*                              CONSTANTS                                     */
/* -------------------------------------------------------------------------- */

const MIN_VISIBLE_DAYS = 5;
const INITIAL_VISIBLE_DAYS = 10;
const ZOOM_STEP = 2;

/* -------------------------------------------------------------------------- */
/*                              LABELS                                        */
/* -------------------------------------------------------------------------- */

const PurpleLabel = (props: any) => {
  const { x, y, value } = props;

  if (x == null || y == null || value == null) {
    return null;
  }

  return (
    <g>
      <rect
        x={x - 13}
        y={y - 24}
        width={26}
        height={16}
        rx={3}
        fill="#7C4DFF"
      />

      <text
        x={x}
        y={y - 13}
        textAnchor="middle"
        fill="#FFFFFF"
        fontSize={10}
        fontWeight={700}
      >
        {Number(value).toFixed(1)}
      </text>
    </g>
  );
};

const GrayLabel = (props: any) => {
  const { x, y, value } = props;

  if (x == null || y == null || value == null) {
    return null;
  }

  return (
    <g>
      <rect
        x={x - 13}
        y={y + 8}
        width={26}
        height={16}
        rx={3}
        fill="#98A2B3"
      />

      <text
        x={x}
        y={y + 19}
        textAnchor="middle"
        fill="#FFFFFF"
        fontSize={10}
        fontWeight={700}
      >
        {Number(value).toFixed(1)}
      </text>
    </g>
  );
};

/* -------------------------------------------------------------------------- */
/*                         CUSTOM X-AXIS TICK                                  */
/* -------------------------------------------------------------------------- */

interface CustomXAxisTickProps {
  x?: number;
  y?: number;
  payload?: {
    value?: string | number;
    index?: number;
  };
  activeIndex?: number | null;
}

const CustomXAxisTick = ({
  x = 0,
  y = 0,
  payload,
  activeIndex,
}: CustomXAxisTickProps) => {
  if (!payload) {
    return null;
  }

  const value =
    payload.value ?? '';

  const index =
    payload.index ?? -1;

  const isActive =
    activeIndex !== null &&
    activeIndex !== undefined &&
    index === activeIndex;

  return (
    <g
      transform={`translate(${x},${y})`}
      pointerEvents="none"
      style={{
        overflow: 'visible',
      }}
    >
      {isActive ? (
        <>
          {/* ============================================================
              HOVER POINTER

              The pointer is centered directly above the
              selected day box, matching the supplied image.
          ============================================================ */}

          <path
            d="
              M -7 6
              L 0 -1
              L 7 6
              Z
            "
            fill="#FFFFFF"
            stroke="#98A2B3"
            strokeWidth={1}
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />

          {/* Remove the lower pointer edge where it
              joins the box. */}
          <rect
            x="-8"
            y="4"
            width="16"
            height="5"
            fill="#FFFFFF"
          />

          {/* ============================================================
              SELECTED DAY BOX

              Same compact box as the requested image:
              white background, thin grey border,
              46px width and 38px height.
          ============================================================ */}

          <rect
            x="-23"
            y="6"
            width="46"
            height="38"
            rx="2"
            fill="#FFFFFF"
            stroke="#98A2B3"
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
          />

          {/* Selected day */}
          <text
            x="0"
            y="27"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#344054"
            fontSize={10}
            fontWeight={400}
          >
            {String(value)
              .substring(0, 3)
              .toUpperCase()}
          </text>
        </>
      ) : (
        /* ==============================================================
           NORMAL X-AXIS LABEL
        ============================================================== */

        <text
          x="0"
          y="20"
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#667085"
          fontSize={11}
          fontWeight={400}
        >
          {value}
        </text>
      )}
    </g>
  );
};

/* -------------------------------------------------------------------------- */
/*                              TOOLTIP                                       */
/* -------------------------------------------------------------------------- */

const CustomTooltip = ({
  active,
  payload,
}: any) => {
  if (!active || !payload?.length) {
    return null;
  }

  const me = payload.find(
    (item: any) => item.dataKey === 'me',
  );

  const team = payload.find(
    (item: any) => item.dataKey === 'team',
  );

  return (
    <div
      className="
        rounded-[10px]
        border
        border-[#E5E7EB]
        bg-white
        px-3
        py-2
        shadow-lg
      "
    >
      {/* Me */}
      <div className="mb-2 flex items-center gap-2">
        <span
          className="
            h-[9px]
            w-[9px]
            rounded-full
            bg-[#7C4DFF]
          "
        />

        <span className="text-[12px] text-[#344054]">
          Me:
        </span>

        <span className="text-[12px] font-semibold text-[#101828]">
          {me?.value ?? '--'}h
        </span>
      </div>

      {/* Team */}
      <div className="flex items-center gap-2">
        <span
          className="
            h-[9px]
            w-[9px]
            rounded-full
            bg-[#98A2B3]
          "
        />

        <span className="text-[12px] text-[#344054]">
          Team Avg:
        </span>

        <span className="text-[12px] font-semibold text-[#101828]">
          {team?.value ?? '--'}h
        </span>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*                         MAIN COMPONENT                                     */
/* -------------------------------------------------------------------------- */

const ActiveHoursHistory30Days = ({
  data,
}: Props) => {
  /* ------------------------------------------------------------------------ */
  /*                              REFS                                        */
  /* ------------------------------------------------------------------------ */

  const chartWheelRef =
    useRef<HTMLDivElement | null>(null);

  /* ------------------------------------------------------------------------ */
  /*                              STATE                                       */
  /* ------------------------------------------------------------------------ */

  const [visibleCount, setVisibleCount] =
    useState(INITIAL_VISIBLE_DAYS);

  const [hoveredSeries, setHoveredSeries] =
    useState<'me' | 'team' | null>(null);

  /* ------------------------------------------------------------------------ */
  /*                         ACTIVE X-AXIS POINT                                */
  /* ------------------------------------------------------------------------ */

  const [
    activeXAxisIndex,
    setActiveXAxisIndex,
  ] = useState<number | null>(null);

  /* ------------------------------------------------------------------------ */
  /*                           CHART DATA                                      */
  /* ------------------------------------------------------------------------ */

  const chartData = useMemo(() => {
    return (data?.history ?? []).map((item) => ({
      day: new Date(item.workDate).getDate(),

      me: Number(
        Number(item.activeHours).toFixed(1),
      ),

      team: Number(
        Number(
          item.teamAvgActiveHours,
        ).toFixed(1),
      ),
    }));
  }, [data]);

  /* ------------------------------------------------------------------------ */
  /*                       RESET VISIBLE DAYS                                  */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    setVisibleCount(
      Math.min(
        INITIAL_VISIBLE_DAYS,
        Math.max(
          chartData.length,
          MIN_VISIBLE_DAYS,
        ),
      ),
    );

    setActiveXAxisIndex(null);
  }, [chartData.length]);

  /* ------------------------------------------------------------------------ */
  /*                       VISIBLE DATA                                        */
  /* ------------------------------------------------------------------------ */

  const visibleData = useMemo(() => {
    if (!chartData.length) {
      return [];
    }

    if (
      chartData.length <= visibleCount
    ) {
      return chartData;
    }

    return chartData.slice(
      chartData.length - visibleCount,
    );
  }, [
    chartData,
    visibleCount,
  ]);

  /* ------------------------------------------------------------------------ */
  /*                     MOUSE WHEEL ZOOM                                     */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    const element =
      chartWheelRef.current;

    if (!element) {
      return;
    }

    const handleWheel = (
      event: WheelEvent,
    ) => {
      /*
       * IMPORTANT:
       * Prevent the browser/page from scrolling.
       */
      event.preventDefault();

      /*
       * Prevent parent containers from
       * receiving the wheel event.
       */
      event.stopPropagation();

      /*
       * Wheel UP
       * deltaY < 0
       *
       * Show fewer days = ZOOM IN
       */
      if (event.deltaY < 0) {
        setVisibleCount((previous) => {
          return Math.max(
            MIN_VISIBLE_DAYS,
            previous - ZOOM_STEP,
          );
        });

        return;
      }

      /*
       * Wheel DOWN
       * deltaY > 0
       *
       * Show more days = ZOOM OUT
       */
      if (event.deltaY > 0) {
        setVisibleCount((previous) => {
          return Math.min(
            chartData.length,
            previous + ZOOM_STEP,
          );
        });
      }
    };

    /*
     * passive:false is VERY IMPORTANT.
     *
     * Without this, preventDefault()
     * may not stop browser scrolling.
     */
    element.addEventListener(
      'wheel',
      handleWheel,
      {
        passive: false,
      },
    );

    return () => {
      element.removeEventListener(
        'wheel',
        handleWheel,
      );
    };
  }, [chartData.length]);

  /* ------------------------------------------------------------------------ */
  /*                       Y AXIS                                             */
  /* ------------------------------------------------------------------------ */

  const values = visibleData.flatMap(
    (item) => [
      item.me,
      item.team,
    ],
  );

  let yMin = 0;
  let yMax = 10;
  let ticks: number[] = [];

  if (values.length) {
    const minValue = Math.min(
      ...values,
    );

    const maxValue = Math.max(
      ...values,
    );

    yMin =
      Math.floor(
        Math.max(0, minValue - 0.5) * 2,
      ) / 2;

    yMax =
      Math.ceil(
        (maxValue + 0.5) * 2,
      ) / 2;

    /*
     * Make sure there is always
     * enough room for the chart.
     */
    if (yMax - yMin < 2) {
      yMax = yMin + 2;
    }

    for (
      let value = yMin;
      value <= yMax;
      value += 0.5
    ) {
      ticks.push(
        Number(value.toFixed(1)),
      );
    }
  }

  /* ------------------------------------------------------------------------ */
  /*                       MONTH LABEL                                        */
  /* ------------------------------------------------------------------------ */

  const monthLabel =
    data?.months?.length >= 2
      ? `${data.months[0].monthName}–${data.months[1].monthName}`
      : data?.months?.length === 1
      ? data.months[0].monthName
      : '';

  /* ------------------------------------------------------------------------ */
  /*                          EMPTY STATE                                      */
  /* ------------------------------------------------------------------------ */

  if (!data || !chartData.length) {
    return (
      <div
        className="
          rounded-[18px]
          border
          border-[#D0D5DD]
          bg-white
          p-4
        "
      >
        <div className="flex items-center gap-2">
          <h3
            className="
              text-[16px]
              font-semibold
              text-[#002B5B]
            "
          >
            30-Day Active Hours History
          </h3>

          <span
            className="
              rounded-full
              bg-[#EEF4FF]
              px-3
              py-[4px]
              text-[10px]
              text-[#2563EB]
            "
          >
            No data available
          </span>
        </div>
      </div>
    );
  }

  /* ------------------------------------------------------------------------ */
  /*                              RENDER                                      */
  /* ------------------------------------------------------------------------ */

  return (
    <div
      className="
        w-full
        rounded-[18px]
        border
        border-[#D0D5DD]
        bg-white
        p-4
      "
    >
      {/* -------------------------------------------------------------------- */}
      {/* HEADER                                                               */}
      {/* -------------------------------------------------------------------- */}

      <div
        className="
          mb-4
          flex
          flex-wrap
          items-center
          gap-2
        "
      >
        <h3
          className="
            text-[16px]
            font-semibold
            text-[#002B5B]
          "
        >
          30-Day Active Hours History
        </h3>

        <span
          className="
            rounded-full
            bg-[#EEF4FF]
            px-3
            py-[4px]
            text-[10px]
            font-medium
            text-[#2563EB]
          "
        >
          Me vs Team Avg · {monthLabel}
        </span>

        {/* Zoom indicator */}
        <span
          className="
            ml-auto
            rounded-full
            bg-[#F9FAFB]
            px-3
            py-[4px]
            text-[10px]
            text-[#667085]
          "
        >
          Showing {visibleData.length} of{' '}
          {chartData.length} days
        </span>
      </div>

      {/* -------------------------------------------------------------------- */}
      {/* LEGEND                                                               */}
      {/* -------------------------------------------------------------------- */}

      <div
        className="
          mb-4
          flex
          items-center
          justify-center
          gap-8
        "
      >
        {/* Me */}
        <div
          className="
            flex
            cursor-pointer
            items-center
            gap-2
          "
          onMouseEnter={() =>
            setHoveredSeries('me')
          }
          onMouseLeave={() =>
            setHoveredSeries(null)
          }
        >
          <span
            className="
              h-3
              w-3
              rounded-full
              bg-[#7C4DFF]
            "
          />

          <span
            className={`
              text-[13px]
              ${
                hoveredSeries === 'me'
                  ? 'font-semibold text-[#111827]'
                  : 'text-[#344054]'
              }
            `}
          >
            Me
          </span>
        </div>

        {/* Team */}
        <div
          className="
            flex
            cursor-pointer
            items-center
            gap-2
          "
          onMouseEnter={() =>
            setHoveredSeries('team')
          }
          onMouseLeave={() =>
            setHoveredSeries(null)
          }
        >
          <span
            className="
              h-3
              w-3
              rounded-full
              bg-[#98A2B3]
            "
          />

          <span
            className={`
              text-[13px]
              ${
                hoveredSeries === 'team'
                  ? 'font-semibold text-[#111827]'
                  : 'text-[#344054]'
              }
            `}
          >
            Team Avg
          </span>
        </div>
      </div>

      {/* -------------------------------------------------------------------- */}
      {/* CHART                                                                */}
      {/* -------------------------------------------------------------------- */}

      <div
        ref={chartWheelRef}
        className="
          relative
          w-full
          overflow-hidden
          rounded-[12px]
        "
        style={{
          /*
           * Prevent touch/trackpad scrolling
           * from scrolling the parent page.
           */
          overscrollBehavior:
            'contain',
        }}
      >
        <ResponsiveContainer
          width="100%"
          height={235}
        >
          <LineChart
            data={visibleData}
            margin={{
              top: 28,
              right: 15,
              left: 5,
              bottom: 10,
            }}
            onMouseMove={(state: any) => {
              const index =
                state?.activeTooltipIndex;

              if (
                index !== undefined &&
                index !== null &&
                Number.isFinite(
                  Number(index),
                )
              ) {
                setActiveXAxisIndex(
                  Number(index),
                );
              }
            }}
            onMouseLeave={() => {
              setActiveXAxisIndex(null);
            }}
          >
            {/* Grid */}
            <CartesianGrid
              vertical={false}
              stroke="#ECEFF3"
              strokeDasharray="0"
            />

            {/* X Axis */}
            <XAxis
              dataKey="day"
              axisLine={{
                stroke: '#D0D5DD',
              }}
              tickLine={false}
              interval={0}
              height={58}
              tickMargin={0}
              tick={
                <CustomXAxisTick
                  activeIndex={
                    activeXAxisIndex
                  }
                />
              }
            />

            {/* Y Axis */}
            <YAxis
              domain={[
                yMin,
                yMax,
              ]}
              ticks={ticks}
              tickFormatter={(value) =>
                `${value}h`
              }
              axisLine={false}
              tickLine={false}
              width={42}
              tick={{
                fontSize: 11,
                fill: '#667085',
              }}
            />

            {/* Tooltip */}
            <Tooltip
              cursor={{
                stroke: '#D0D5DD',
                strokeDasharray:
                  '4 4',
              }}
              content={
                <CustomTooltip />
              }
            />

            {/* ---------------------------------------------------------------- */}
            {/* ME                                                               */}
            {/* ---------------------------------------------------------------- */}

            <Line
              type="monotone"
              dataKey="me"
              name="Me"
              stroke="#7C4DFF"
              strokeWidth={
                hoveredSeries === 'me'
                  ? 4
                  : 2.5
              }
              strokeOpacity={
                hoveredSeries === null ||
                hoveredSeries === 'me'
                  ? 1
                  : 0.2
              }
              dot={{
                r: 3,
                fill: '#7C4DFF',
                strokeWidth: 0,
              }}
              activeDot={{
                r: 6,
              }}
              label={
                <PurpleLabel />
              }
              isAnimationActive={
                false
              }
            />

            {/* ---------------------------------------------------------------- */}
            {/* TEAM AVG                                                         */}
            {/* ---------------------------------------------------------------- */}

            <Line
              type="monotone"
              dataKey="team"
              name="Team Avg"
              stroke="#98A2B3"
              strokeWidth={
                hoveredSeries === 'team'
                  ? 3
                  : 2
              }
              strokeDasharray="4 4"
              strokeOpacity={
                hoveredSeries === null ||
                hoveredSeries === 'team'
                  ? 1
                  : 0.2
              }
              dot={{
                r: 3,
                fill: '#98A2B3',
                strokeWidth: 0,
              }}
              activeDot={{
                r: 6,
              }}
              label={
                <GrayLabel />
              }
              isAnimationActive={
                false
              }
            />
          </LineChart>
        </ResponsiveContainer>

        {/* ------------------------------------------------------------------ */}
        {/* ZOOM HELP                                                          */}
        {/* ------------------------------------------------------------------ */}

        {/* <div
          className="
            pointer-events-none
            absolute
            bottom-2
            right-3
            rounded-full
            bg-white/90
            px-2
            py-1
            text-[9px]
            text-[#667085]
            shadow-sm
          "
        >
          Scroll ↑ Zoom in · Scroll ↓ Zoom out
        </div> */}
      </div>
    </div>
  );
};

export default ActiveHoursHistory30Days;